import { usePropostasListarKanban } from "services/api/Funil_Propostas/kanban-listar-get";
import { useState, useEffect } from "react";
import { Skeleton, CircularProgress, Tooltip } from "@mui/material";
import { format } from "date-fns";
import { RootStateOrAny, useSelector } from "react-redux";
import { AuthState } from "store/ducks/auth/types";
import { obterNomeEtapaExibicao } from "utils/obterNomeEtapaExibicao";
import * as S from "./styles";
import ItemCard from "./components/ItemCard";

interface Data {
  codigoEtapa: string;
  nomeEtapa: string;
  searchValue: string;
  filterAction: boolean;
  setFilterAction: (value: boolean) => void;
  selectedEtapa: any;
  selectedSubetapa: any;
  selectedEmpreendimento: any;
  selectedBloco: any;
  selectedUnidade: any;
  selectedOrigens: any;
  selectedSuperintendente: any;
  selectedEquipe: any;
  selectedCorretor: any;
  selectedTipoFiltroData: any;
  dataInicio: Date | null;
  dataFim: Date | null;
  checkCompartilharProposta: boolean;
  onQuantidadeLoaded?: (
    codigo: string,
    quantidade: number,
    paginaAtual: number
  ) => void;
}

const EtapaCard = (data: Data) => {
  const toApiDate = (date: Date | null) => {
    if (!date) return undefined;
    return format(date, "yyyy-MM-dd");
  };

  const { usuarioVivaz } = useSelector<RootStateOrAny, AuthState>(
    (state) => state.user
  );

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [items, setItems] = useState<any[]>([]);
  const [quantidadeEtapa, setQuantidadeEtapa] = useState<string>("0");
  const [vgv, setVgv] = useState<string>("0");

  const {
    data: dataKanbanListar,
    refetch: refetchKanbanListar,
    isFetching: isFetchingKanbanListar,
  } = usePropostasListarKanban({
    queryKey: `${data.nomeEtapa}-${paginaAtual}`,
    codigo: data.codigoEtapa,
    Etapa: data.selectedEtapa?.value || "",
    tipoFiltroData: data.selectedTipoFiltroData
      ? Number(data.selectedTipoFiltroData.value)
      : undefined,
    dataInicio: data.selectedTipoFiltroData
      ? toApiDate(data.dataInicio)
      : undefined,
    dataFim: data.selectedTipoFiltroData ? toApiDate(data.dataFim) : undefined,
    SubEtapa:
      data.selectedSubetapa?.map((subEtapa: any) => subEtapa.value) || [],
    IdBloco: data.selectedBloco?.value || "",
    IdUnidade: data.selectedUnidade?.value || "",
    IdsOrigens: data.selectedOrigens?.map((origem: any) => origem.value) || [],
    IdSuperintendente365: data.selectedSuperintendente?.value || "",
    IdGerente365: data.selectedEquipe?.value || "",
    IdCorretor365: data.selectedCorretor?.value || "",
    compartilhar: data.checkCompartilharProposta || false,
    IdEmpreendimento:
      data.selectedEmpreendimento?.map(
        (empreendimento: any) => empreendimento.value
      ) || [],
    Comercial: false,
    PalavraChave: data.searchValue,
    PaginaAtual: paginaAtual,
    TotalItensRetorno: 3,
  });

  useEffect(() => {
    if (dataKanbanListar && !isFetchingKanbanListar) {
      const qtd = dataKanbanListar.data?.quantidadeEtapa;
      const vgvValue = dataKanbanListar.data?.vgv;
      if (qtd !== undefined) setQuantidadeEtapa(String(qtd));
      if (vgvValue !== undefined) setVgv(String(vgvValue || "0"));
      data.onQuantidadeLoaded?.(data.codigoEtapa, qtd || 0, paginaAtual);
    }
  }, [dataKanbanListar, isFetchingKanbanListar]);

  useEffect(() => {
    if (data.filterAction) {
      setPaginaAtual(1);
      setItems([]);
      refetchKanbanListar();
      data.setFilterAction(false);
    }
  }, [data.filterAction]);

  useEffect(() => {
    if (paginaAtual > 1) {
      refetchKanbanListar();
    }
  }, [paginaAtual]);

  useEffect(() => {
    if (dataKanbanListar && !isFetchingKanbanListar) {
      const novaListaFila = dataKanbanListar.data?.listaFila ?? [];
      if (paginaAtual === 1) {
        setItems(novaListaFila);
      } else {
        setItems((prevItems) => [...prevItems, ...novaListaFila]);
      }
    }
  }, [dataKanbanListar, isFetchingKanbanListar, paginaAtual]);

  const loadMoreItems = () => {
    if (items.length < (dataKanbanListar?.pagination?.totalItems || 0)) {
      setPaginaAtual((prev) => prev + 1);
    }
  };

  const handleScroll = (event: any) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (
      scrollHeight - scrollTop <= clientHeight + 5 &&
      !isFetchingKanbanListar
    ) {
      loadMoreItems();
    }
  };

  return (
    <S.Container onScroll={handleScroll}>
      <S.Header>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Tooltip title={obterNomeEtapaExibicao(data.nomeEtapa, usuarioVivaz)}>
            <S.TextEtapa
              style={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                maxWidth: "100px",
              }}
            >
              {obterNomeEtapaExibicao(data.nomeEtapa, usuarioVivaz)}
            </S.TextEtapa>
          </Tooltip>
          <S.TextEtapa>({quantidadeEtapa})</S.TextEtapa>
        </div>
        <div>
          <S.TextEtapa>VGV {vgv}</S.TextEtapa>
        </div>
      </S.Header>
      <S.SpaceY space={12} />
      {isFetchingKanbanListar && items.length === 0 && (
        <S.BodySkeleton>
          <Skeleton variant="rounded" width={271} height={340} />
        </S.BodySkeleton>
      )}
      {items.map((item: any) => (
        <ItemCard
          key={item.numeroCotacao}
          listaFila={item}
          isFetchingKanbanListar={isFetchingKanbanListar}
        />
      ))}
      {isFetchingKanbanListar && items.length > 0 && (
        <S.LoadingContainer>
          <CircularProgress size={24} />
        </S.LoadingContainer>
      )}
      <S.SpaceY space={12} />
    </S.Container>
  );
};

export default EtapaCard;
